/**
 * 맥스매치 운영 상세 — 처방 이미지별 OCR·의약품 매칭·확정 및 max match 실행.
 */
import type { MaxMatchOpsMr } from '@/entities/max-match/api/max-match-ops.api';
import { maxMatchOpsService } from '@/entities/max-match/api/max-match-ops.api';
import {
  useAddMaxMatchOpsMrsBatch,
  useConfirmMaxMatchOpsBatch,
  useConfirmMaxMatchOpsImage,
  useDeleteAllMaxMatchOpsMrs,
  useDeleteMaxMatchOpsImage,
  useDeleteMaxMatchOpsMr,
  useDeleteMaxMatchOpsSummary,
  useMaxMatchOpsDetail,
  useMaxMatchOpsImages,
  useRunMaxMatchOps,
  useUpdateMaxMatchOpsMr,
} from '@/entities/max-match/hooks/useMaxMatchOps';
import { ocrService } from '@/entities/ocr/api/ocr.api';
import { preprocessService } from '@/entities/ocr/api/preprocess.api';
import { useMenuAccess } from '@/layouts/MenuAccessContext';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { FilterCard } from '@/shared/components/FilterCard';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  OcrCropWarpUploader,
  type OcrCropWarpFileItem,
  type OcrCropWarpSelection,
} from '@/shared/components/OcrCropWarpUploader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import { Button } from '@/shared/components/ui/button';
import { CardContent } from '@/shared/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import { EditedOcrComparisonModal } from '@/pages/prescriptions/EditedOcrComparisonModal';
import {
  normalizeEditedOcrItems,
  type EditedOcrDrugRow,
} from '@/pages/prescriptions/prescriptionOcrUtils';
import type { DrugMatchingResultItem } from '@/pages/prescriptions/prescriptionStagingUtils';
import {
  MaxMatchMiniDrugDialog,
  type MaxMatchMiniDrugFormValues,
} from '@/pages/max-match/MaxMatchMiniDrugDialog';
import {
  applyMiniDrugMatchingResults,
  calcMrTotalAmount,
  formatMiniAmount,
  getDuplicatedDrugGroups,
  getMrRowKey,
  mapEditedOcrRowsToMrs,
  mergeDrugsByCode,
  withMiniTempRowKey,
} from '@/pages/max-match/maxMatchMiniUtils';
import {
  getBoundingBoxFromPoints,
  getInitialOcrCornerPoints,
} from '@/shared/utils/ocrCropWarpUtils';
import type { Point2D } from '@/shared/utils/ocrImagePreprocess';
import {
  buildOcrCoordinateStorageKey,
  loadOcrCoordinatesFromSession,
  OCR_COORDINATE_STORAGE_PREFIX,
  saveOcrCoordinatesToSession,
} from '@/shared/lib/ocrCoordinateStorage';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { formatDateTime, formatYearMonth } from '@/shared/lib/dateFormat';
import { useUIStore } from '@/stores/uiStore';
import { useNavigate, useParams, useRouter } from '@tanstack/react-router';
import {
  Check,
  Download,
  ExternalLink,
  Play,
  Plus,
  ScanLine,
  Sparkles,
  Trash2,
} from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FooterRow, ToolbarRow } from '@/shared/components/ui/row';
import {
  downloadUriFile,
  openFilePreview,
} from '@/shared/components/DownloadWithPreview';

type ConfirmKind =
  | 'deleteDrug'
  | 'deleteAll'
  | 'ocrOverwrite'
  | 'editedOcrOverwrite'
  | 'deleteImage'
  | 'deleteSummary'
  | 'leave'
  | null;

const statusLabel = (status?: string): string => {
  switch (status) {
    case 'BEFORE_MATCH':
      return '매칭 전';
    case 'MATCHED':
      return '매칭 완료';
    default:
      return status || '-';
  }
};

export const MaxMatchOpsDetailPage: React.FC = () => {
  const toast = useToast();
  const { readOnly } = useMenuAccess();
  const setGlobalLoading = useUIStore((s) => s.setGlobalLoading);
  const navigate = useNavigate();
  const router = useRouter();
  const { summaryId: summaryIdParam } = useParams({ strict: false }) as {
    summaryId?: string;
  };
  const summaryId = Number(summaryIdParam ?? 0);

  const { data: detail, isLoading: detailLoading, refetch: refetchDetail } =
    useMaxMatchOpsDetail(summaryId);
  const { data: images = [], isLoading: imagesLoading, refetch: refetchImages } =
    useMaxMatchOpsImages(summaryId);

  const [selectedImageId, setSelectedImageId] = useState(0);
  const [drugData, setDrugData] = useState<MaxMatchOpsMr[]>([]);
  const [drugLoading, setDrugLoading] = useState(false);
  const [plainOcrPending, setPlainOcrPending] = useState(false);
  const [pendingEditedOcrSource, setPendingEditedOcrSource] = useState<
    'default' | 'gemini' | null
  >(null);

  const [drugDialogOpen, setDrugDialogOpen] = useState(false);
  const [editingDrug, setEditingDrug] = useState<MaxMatchOpsMr | null>(null);
  const [editingOcrIndex, setEditingOcrIndex] = useState<number | null>(null);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    record: MaxMatchOpsMr;
    index: number;
  } | null>(null);
  const [ocrFileDialogOpen, setOcrFileDialogOpen] = useState(false);
  const [editedOcrImages, setEditedOcrImages] = useState<OcrCropWarpFileItem[]>([]);
  const [ocrLiveSelection, setOcrLiveSelection] =
    useState<OcrCropWarpSelection | null>(null);
  const [ocrSourceInitialPoints, setOcrSourceInitialPoints] = useState<Point2D[]>([]);
  const [isPreparingOcrImageEditor, setIsPreparingOcrImageEditor] = useState(false);
  const [showDuplicateMergeModal, setShowDuplicateMergeModal] = useState(false);
  const [drugMatching, setDrugMatching] = useState(false);
  const [editedOcrComparing, setEditedOcrComparing] = useState(false);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<{
    default: EditedOcrDrugRow[];
    gemini: EditedOcrDrugRow[];
  }>({ default: [], gemini: [] });
  const [editedOcrRequestPreviewUrl, setEditedOcrRequestPreviewUrl] = useState<
    string | null
  >(null);
  const ocrCoordinatesFetchKeyRef = useRef<string | null>(null);

  const confirmImage = useConfirmMaxMatchOpsImage(summaryId);
  const confirmBatch = useConfirmMaxMatchOpsBatch(summaryId);
  const addMrsBatch = useAddMaxMatchOpsMrsBatch(summaryId);
  const deleteMr = useDeleteMaxMatchOpsMr(summaryId);
  const deleteAllMrs = useDeleteAllMaxMatchOpsMrs(summaryId);
  const updateMr = useUpdateMaxMatchOpsMr(summaryId);
  const runOps = useRunMaxMatchOps();
  const deleteImage = useDeleteMaxMatchOpsImage(summaryId);
  const deleteSummary = useDeleteMaxMatchOpsSummary();

  const selectedImage = useMemo(
    () => images.find((img) => img.imageId === selectedImageId) ?? null,
    [images, selectedImageId],
  );

  // 레거시 max-match-ops-ocr-coordinates sessionStorage 복원
  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchOps,
    );
    const stored = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (stored) {
      setOcrSourceInitialPoints(stored.points);
      ocrCoordinatesFetchKeyRef.current = sourceImageUrl ?? null;
    } else {
      setOcrSourceInitialPoints([]);
      ocrCoordinatesFetchKeyRef.current = null;
    }
  }, [selectedImage?.imageCode, selectedImage?.imageUrl]);

  useEffect(() => {
    const sourceImageUrl = selectedImage?.imageUrl;
    if (
      !sourceImageUrl ||
      ocrSourceInitialPoints.length !== 4 ||
      !selectedImage?.imageCode
    ) {
      return;
    }
    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchOps,
    );
    saveOcrCoordinatesToSession(storageKey, {
      sourceImageUrl,
      points: ocrSourceInitialPoints,
      bbox: getBoundingBoxFromPoints(ocrSourceInitialPoints),
    });
  }, [
    ocrSourceInitialPoints,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
  ]);

  useEffect(() => {
    const busy =
      isPreparingOcrImageEditor ||
      plainOcrPending ||
      editedOcrComparing ||
      drugMatching;
    setGlobalLoading(
      busy,
      isPreparingOcrImageEditor
        ? '편집 영역을 준비하는 중...'
        : drugMatching
          ? '의약품 매칭 요청 중...'
          : editedOcrComparing
            ? '편집 이미지 OCR 비교 요청 중...'
            : 'OCR 요청 중...',
    );
    return () => setGlobalLoading(false);
  }, [
    isPreparingOcrImageEditor,
    plainOcrPending,
    editedOcrComparing,
    drugMatching,
    setGlobalLoading,
  ]);

  const confirmedCount = useMemo(
    () => images.filter((img) => img.isConfirmed).length,
    [images],
  );

  const hasUnsavedDrugs = useMemo(
    () => drugData.some((item) => item.mrId == null),
    [drugData],
  );

  const duplicatedDrugGroups = useMemo(
    () => getDuplicatedDrugGroups(drugData),
    [drugData],
  );

  // 매칭 완료 후에는 나가기·운영 건 삭제만 가능
  const isMatched = detail?.status === 'MATCHED';
  const actionDisabled = readOnly || isMatched;
  const canRun =
    !isMatched && images.length > 0 && confirmedCount === images.length;

  useEffect(() => {
    if (images.length > 0 && !selectedImageId) {
      setSelectedImageId(images[0].imageId);
    }
  }, [images, selectedImageId]);

  const fetchMrsData = useCallback(async (imageId: number) => {
    if (!imageId) return;
    setDrugLoading(true);
    try {
      const data = await maxMatchOpsService.getMrs(imageId);
      setDrugData(data.map(withMiniTempRowKey));
    } catch (e) {
      handleApiError(e);
    } finally {
      setDrugLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedImageId) {
      void fetchMrsData(selectedImageId);
    }
  }, [selectedImageId, fetchMrsData]);

  const handleDeleteAllMrs = async () => {
    if (!selectedImageId) return;
    try {
      await deleteAllMrs.mutateAsync(selectedImageId);
      setDrugData([]);
      await refetchImages();
      toast.success('의약품이 삭제되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
    }
  };

  /** 레거시 runPlainOcr — Document AI process-url */
  const fetchPlainOcrMrs = async (): Promise<MaxMatchOpsMr[]> => {
    const raw = await ocrService.ocrPrescriptionFromUrl({
      imageUrl: selectedImage!.imageUrl!,
    });
    const rows = normalizeEditedOcrItems(raw);
    if (rows.length === 0) {
      throw new Error('OCR 결과가 없습니다.');
    }
    return mapEditedOcrRowsToMrs(rows);
  };

  const runOcr = async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('이미지가 선택되지 않았습니다.');
      return;
    }
    setPlainOcrPending(true);
    try {
      const drugList = await fetchPlainOcrMrs();
      setDrugData(drugList);
      toast.success('OCR 요청이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setPlainOcrPending(false);
    }
  };

  const fetchInitialOcrCoordinates = useCallback(async () => {
    const sourceImageUrl = selectedImage?.imageUrl;
    const fetchKey = sourceImageUrl || null;
    if (!fetchKey) {
      setOcrSourceInitialPoints([]);
      return false;
    }

    const storageKey = buildOcrCoordinateStorageKey(
      selectedImage?.imageCode,
      OCR_COORDINATE_STORAGE_PREFIX.maxMatchOps,
    );
    const cached = loadOcrCoordinatesFromSession(storageKey, sourceImageUrl);
    if (cached) {
      ocrCoordinatesFetchKeyRef.current = fetchKey;
      setOcrSourceInitialPoints(cached.points);
      return true;
    }

    if (ocrCoordinatesFetchKeyRef.current === fetchKey) {
      return ocrSourceInitialPoints.length === 4;
    }

    setIsPreparingOcrImageEditor(true);
    try {
      const response = await preprocessService.getCornersFromUrl(sourceImageUrl!);
      const nextPoints = getInitialOcrCornerPoints(response);
      if (nextPoints.length === 4) {
        ocrCoordinatesFetchKeyRef.current = fetchKey;
        setOcrSourceInitialPoints(nextPoints);
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: sourceImageUrl!,
          points: nextPoints,
          bbox: getBoundingBoxFromPoints(nextPoints),
        });
        return true;
      }
      setOcrSourceInitialPoints([]);
      return false;
    } catch (e) {
      console.error('Failed to prepare OCR coordinates:', e);
      toast.error('OCR 좌표를 가져오지 못했습니다. 기본 영역으로 편집할 수 있습니다.');
      setOcrSourceInitialPoints([]);
      return false;
    } finally {
      setIsPreparingOcrImageEditor(false);
    }
  }, [
    ocrSourceInitialPoints.length,
    selectedImage?.imageCode,
    selectedImage?.imageUrl,
    toast,
  ]);

  const handleOpenOcrImageEditor = useCallback(async () => {
    if (!selectedImage?.imageUrl) {
      toast.error('편집할 원본 이미지가 없습니다.');
      return;
    }
    setEditedOcrImages([]);
    await fetchInitialOcrCoordinates();
    setOcrFileDialogOpen(true);
  }, [fetchInitialOcrCoordinates, selectedImage?.imageUrl, toast]);

  /** 레거시/처방검수: 닫을 때 편집 corners를 다음 오픈용 source로 재반영 */
  const closeOcrImageEditor = useCallback(() => {
    const meta = editedOcrImages[0]?.ocrMeta;
    const live = ocrLiveSelection;
    const corners = meta?.corners?.length === 4 ? meta.corners : live?.corners;
    const bbox =
      meta?.corners?.length === 4
        ? (meta.bbox ?? getBoundingBoxFromPoints(meta.corners))
        : live?.bbox;
    if (corners?.length === 4) {
      setOcrSourceInitialPoints(corners);
      if (selectedImage?.imageUrl) {
        const storageKey = buildOcrCoordinateStorageKey(
          selectedImage.imageCode || String(selectedImage.imageId),
          OCR_COORDINATE_STORAGE_PREFIX.maxMatchOps,
        );
        saveOcrCoordinatesToSession(storageKey, {
          sourceImageUrl: selectedImage.imageUrl,
          points: corners,
          bbox: bbox ?? getBoundingBoxFromPoints(corners),
        });
      }
    }
    setOcrFileDialogOpen(false);
    setEditedOcrImages([]);
    setOcrLiveSelection(null);
  }, [editedOcrImages, ocrLiveSelection, selectedImage]);

  const handleOcr = async () => {
    if (drugData.length > 0) {
      setConfirmKind('ocrOverwrite');
      return;
    }
    await runOcr();
  };

  const runEditedOcrComparison = async () => {
    const imageUrl = selectedImage?.imageUrl;
    const meta = editedOcrImages[0]?.ocrMeta;
    const corners = meta?.corners ?? ocrLiveSelection?.corners;
    const bbox = meta?.bbox ?? ocrLiveSelection?.bbox;
    const nextPreviewUrl =
      editedOcrImages[0]?.preview || ocrLiveSelection?.previewUrl || '';

    if (
      !imageUrl ||
      !bbox ||
      !Array.isArray(corners) ||
      corners.length !== 4
    ) {
      toast.error('OCR 좌표 영역을 먼저 선택해 주세요.');
      return;
    }

    setEditedOcrComparing(true);
    try {
      let previewForModal = nextPreviewUrl || null;
      if (nextPreviewUrl?.startsWith('blob:')) {
        try {
          const blob = await (await fetch(nextPreviewUrl)).blob();
          previewForModal = URL.createObjectURL(blob);
        } catch {
          previewForModal = null;
        }
      }

      const payload = { imageUrl, corners, bbox };
      const [defaultResult, geminiResult] = await Promise.allSettled([
        ocrService.ocrPrescriptionFromUrl(payload),
        ocrService.ocrPrescriptionFromUrlGemini(payload),
      ]);

      const normalizedDefault =
        defaultResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(defaultResult.value)
          : [];
      const normalizedGemini =
        geminiResult.status === 'fulfilled'
          ? normalizeEditedOcrItems(geminiResult.value)
          : [];

      if (normalizedDefault.length === 0 && normalizedGemini.length === 0) {
        if (previewForModal?.startsWith('blob:')) {
          URL.revokeObjectURL(previewForModal);
        }
        throw new Error(
          'Document AI와 Gemini OCR 결과를 모두 가져오지 못했습니다.',
        );
      }

      setEditedOcrRequestPreviewUrl((prev) => {
        if (prev?.startsWith('blob:') && prev !== previewForModal) {
          URL.revokeObjectURL(prev);
        }
        return previewForModal;
      });
      setComparisonResults({
        default: normalizedDefault,
        gemini: normalizedGemini,
      });
      setComparisonOpen(true);
      setOcrFileDialogOpen(false);
      setEditedOcrImages([]);
      setOcrLiveSelection(null);

      if (
        defaultResult.status === 'rejected' ||
        geminiResult.status === 'rejected'
      ) {
        toast.error(
          '일부 OCR 요청이 실패했습니다. 조회된 결과만 비교 후 선택해 주세요.',
        );
      } else {
        toast.success('OCR 비교 결과를 불러왔습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setEditedOcrComparing(false);
    }
  };

  const applyEditedOcrRows = (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    setDrugData(mapEditedOcrRowsToMrs(selected));
    setComparisonOpen(false);
    setPendingEditedOcrSource(null);
    toast.success(
      `${source === 'gemini' ? 'Gemini' : 'Document AI'} OCR 결과를 반영했습니다.`,
    );
  };

  const applyEditedOcrResult = async (source: 'default' | 'gemini') => {
    const selected = comparisonResults[source] ?? [];
    if (!selected.length) {
      toast.error('적용할 OCR 결과가 없습니다.');
      return;
    }

    // 레거시: 기존 약이 있으면 확인 후 삭제·적용
    if (drugData.length > 0 && selectedImageId) {
      setPendingEditedOcrSource(source);
      setConfirmKind('editedOcrOverwrite');
      return;
    }

    applyEditedOcrRows(source);
  };

  const submitDrugBatch = async (rows: MaxMatchOpsMr[]) => {
    if (!selectedImageId) return;
    try {
      await addMrsBatch.mutateAsync({
        imageId: selectedImageId,
        payload: rows.map((item) => ({
          drugCode: item.drugCode,
          ...(item.drugName ? { drugName: item.drugName } : {}),
          outHospitalQuantity: item.outHospitalQuantity ?? 0,
          inHospitalQuantity: item.inHospitalQuantity ?? 0,
        })),
      });
      await fetchMrsData(selectedImageId);
      // MR 등록 시 이미지 확정 상태가 해제되므로 목록을 다시 조회한다
      await refetchImages();
      toast.success('의약품이 등록되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleBatchAdd = async () => {
    if (!selectedImageId) return;
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    if (pendingDrugs.length === 0) {
      toast.error('일괄 등록할 의약품이 없습니다.');
      return;
    }
    if (pendingDrugs.some((item) => !item.drugCode)) {
      toast.error('의약품 코드가 비어있는 의약품이 있습니다.');
      return;
    }
    if (duplicatedDrugGroups.length > 0) {
      setShowDuplicateMergeModal(true);
      return;
    }
    await submitDrugBatch(pendingDrugs);
  };

  const handleConfirmDuplicateMerge = async () => {
    setShowDuplicateMergeModal(false);
    const pendingDrugs = drugData.filter((item) => item.mrId == null);
    await submitDrugBatch(mergeDrugsByCode(pendingDrugs));
  };

  const handleDrugMatching = async () => {
    if (drugData.length === 0) {
      toast.error('매칭할 의약품이 없습니다.');
      return;
    }
    if (!detail?.refYm) {
      toast.error('처방 기준 년월 정보가 없습니다.');
      return;
    }

    setDrugMatching(true);
    try {
      const items = drugData.map((item) => ({
        drugName: item.drugName ?? '',
        ...(item.drugCode ? { drugCode: item.drugCode } : {}),
        ...(item.pharmaCompanyName
          ? { pharmaCompanyName: item.pharmaCompanyName }
          : {}),
        ...(item.price != null ? { unitPrice: item.price } : {}),
        ...(item.inHospitalQuantity != null
          ? { inQuantity: item.inHospitalQuantity }
          : {}),
        ...(item.outHospitalQuantity != null
          ? { outQuantity: item.outHospitalQuantity }
          : {}),
      }));

      const response = await ocrService.searchDrugMatching({
        items,
        limit: 5,
        yearMonth: detail.refYm,
      });

      if (response.metadata?.confidence === 'no_data') {
        toast.error(
          response.metadata.reason ||
            '해당 연월의 마스터 데이터가 없어 매칭할 수 없습니다.',
        );
        return;
      }

      const results = response.results as DrugMatchingResultItem[];
      if (results.length === 0) {
        toast.error('매칭 결과가 없습니다.');
        return;
      }

      const { nextList, matchedCount } = applyMiniDrugMatchingResults(
        drugData,
        results,
      );
      setDrugData(nextList);
      toast.success(
        `의약품 매칭 완료 (${matchedCount}/${drugData.length}건)`,
      );
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setDrugMatching(false);
    }
  };

  const handleConfirmImage = async () => {
    if (!selectedImageId) return;
    if (selectedImage?.isConfirmed) return;
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    if (!drugData.every((item) => item.hasDrugInfo === true)) {
      toast.error('의약품 정보가 존재하지 않는 의약품이 있습니다.');
      return;
    }
    try {
      await confirmImage.mutateAsync(selectedImageId);
      await refetchImages();
      toast.success('확정이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleConfirmAll = async () => {
    if (hasUnsavedDrugs) {
      toast.error('의약품 정보가 저장되지 않은 의약품이 있습니다.');
      return;
    }
    const unconfirmedIds = images
      .filter((img) => !img.isConfirmed)
      .map((img) => img.imageId);
    if (unconfirmedIds.length === 0) {
      toast.error('확정할 이미지가 없습니다.');
      return;
    }
    try {
      await confirmBatch.mutateAsync(unconfirmedIds);
      await refetchImages();
      toast.success('전체 확정이 완료되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleRunMaxMatch = async () => {
    if (runOps.isPending) return;
    try {
      const result = await runOps.mutateAsync(summaryId);
      await refetchDetail();
      toast.success(
        `max match 생성이 완료되었습니다. (예상 ${result.originEstimatedTotalAmount.toLocaleString()}원 → 매칭 ${result.newEstimatedTotalAmount.toLocaleString()}원, 유지 ${result.unchangedCount}건 / 변경 ${result.changedCount}건)`,
      );
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedImageId) return;
    try {
      await deleteImage.mutateAsync(selectedImageId);
      setSelectedImageId(0);
      toast.success('이미지가 삭제되었습니다.');
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
    }
  };

  const handleDeleteSummary = async () => {
    if (!summaryId) return;
    try {
      await deleteSummary.mutateAsync(summaryId);
      toast.success('운영 건이 삭제되었습니다.');
      navigate({ to: ROUTE_PATHS.MAX_MATCH_OPS } as never);
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
    }
  };

  const handleDrugSubmit = async (values: MaxMatchMiniDrugFormValues) => {
    if (!selectedImageId) return;

    if (editingOcrIndex != null) {
      setDrugData((prev) => {
        const next = [...prev];
        const existing = next[editingOcrIndex];
        next[editingOcrIndex] = {
          ...existing,
          drugCode: values.drugCode,
          drugName: values.drugName || existing.drugName,
          pharmaCompanyName:
            values.pharmaCompanyName || existing.pharmaCompanyName,
          price: values.price ?? existing.price,
          outHospitalQuantity: values.outHospitalQuantity,
          inHospitalQuantity: values.inHospitalQuantity,
          hasDrugInfo: null,
        };
        return next;
      });
      setDrugDialogOpen(false);
      setEditingOcrIndex(null);
      toast.success('수정되었습니다.');
      return;
    }

    if (!editingDrug) {
      setDrugData((prev) => [
        ...prev,
        withMiniTempRowKey({
          mrId: undefined,
          drugCode: values.drugCode,
          drugName: values.drugName || undefined,
          pharmaCompanyName: values.pharmaCompanyName || undefined,
          price: values.price,
          outHospitalQuantity: values.outHospitalQuantity,
          inHospitalQuantity: values.inHospitalQuantity,
          hasDrugInfo: null,
        }),
      ]);
      setDrugDialogOpen(false);
      toast.success('추가되었습니다.');
      return;
    }

    try {
      if (editingDrug.mrId != null) {
        const remainingUnsaved = drugData.filter((item) => item.mrId == null);
        await updateMr.mutateAsync({
          mrId: editingDrug.mrId,
          imageId: selectedImageId,
          payload: {
            drugCode: values.drugCode,
            ...(values.drugName ? { drugName: values.drugName } : {}),
            outHospitalQuantity: values.outHospitalQuantity,
            inHospitalQuantity: values.inHospitalQuantity,
          },
        });
        await fetchMrsData(selectedImageId);
        // MR 수정 시 이미지 확정 상태가 해제되므로 목록을 다시 조회한다
        await refetchImages();
        if (remainingUnsaved.length > 0) {
          setDrugData((prev) => [...prev, ...remainingUnsaved]);
        }
        toast.success('수정되었습니다.');
      }
      setDrugDialogOpen(false);
    } catch (e) {
      handleApiError(e, toast);
    }
  };

  const handleDeleteDrug = async () => {
    if (!pendingDelete) return;
    const { record, index } = pendingDelete;
    try {
      if (record.mrId == null) {
        setDrugData((prev) => prev.filter((_, i) => i !== index));
        toast.success('삭제되었습니다.');
      } else {
        const remainingUnsaved = drugData.filter((item) => item.mrId == null);
        await deleteMr.mutateAsync({
          mrId: record.mrId,
          imageId: selectedImageId,
        });
        await fetchMrsData(selectedImageId);
        // MR 삭제 시 이미지 확정 상태가 해제되므로 목록을 다시 조회한다
        await refetchImages();
        if (remainingUnsaved.length > 0) {
          setDrugData((prev) => [...prev, ...remainingUnsaved]);
        }
        toast.success('삭제되었습니다.');
      }
    } catch (e) {
      handleApiError(e, toast);
    } finally {
      setConfirmKind(null);
      setPendingDelete(null);
    }
  };

  const isOcrBusy =
    plainOcrPending ||
    editedOcrComparing ||
    drugMatching ||
    deleteAllMrs.isPending;

  if (imagesLoading || (detailLoading && !detail)) {
    return <LoadingSpinner tip="맥스매치 운영 상세를 불러오는 중..." />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Sparkles}
        title="맥스매치 운영 상세"
        description={`운영 건 #${summaryId}`}
        actions={
          <>
            <Button
              size="sm"
              variant="outline"
              className="min-w-[7.5rem]"
              onClick={() => {
                if (hasUnsavedDrugs) {
                  setConfirmKind('leave');
                  return;
                }
                if (window.history.length > 1) {
                  router.history.back();
                } else {
                  navigate({ to: ROUTE_PATHS.MAX_MATCH_OPS } as never);
                }
              }}
            >
              나가기
            </Button>
            <MenuWriteGate>
              <Button
                size="sm"
                variant="destructive"
                className="min-w-[7.5rem]"
                disabled={readOnly}
                onClick={() => setConfirmKind('deleteSummary')}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                운영 건 삭제
              </Button>
            </MenuWriteGate>
            <MenuWriteGate>
              <Button
                size="sm"
                className="ruda-button min-w-[7.5rem]"
                disabled={
                  actionDisabled ||
                  images.length === 0 ||
                  confirmedCount === images.length ||
                  hasUnsavedDrugs
                }
                onClick={handleConfirmAll}
              >
                전체 확정 완료
              </Button>
            </MenuWriteGate>
          </>
        }
      />

      <DataCard>
        <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label>제공업체명</Label>
            <Input value={detail?.pharmaCompanyName ?? ''} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>병의원명</Label>
            <Input value={detail?.clinicName ?? ''} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>처방 기준 년월</Label>
            <Input value={formatYearMonth(detail?.refYm)} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>상태</Label>
            <Input value={statusLabel(detail?.status)} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>등록일시</Label>
            <Input value={formatDateTime(detail?.createdAt)} disabled readOnly />
          </div>
          <div className="space-y-1">
            <Label>매칭일시</Label>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                value={formatDateTime(detail?.matchedAt)}
                disabled
                readOnly
                className="min-w-0 flex-1"
              />
              <MenuWriteGate>
                <Button
                  size="sm"
                  className="ruda-button shrink-0"
                  disabled={!canRun || runOps.isPending || actionDisabled}
                  onClick={() => void handleRunMaxMatch()}
                >
                  <Play className="mr-2 h-4 w-4" />
                  max match 생성
                </Button>
              </MenuWriteGate>
            </div>
          </div>
        </CardContent>
      </DataCard>

      <FilterCard>
        <ToolbarRow justify="between">
          <div className="flex flex-wrap items-center gap-2">
            <Label className="text-destructive">처방 이미지 선택</Label>
            <Select
              value={selectedImageId > 0 ? String(selectedImageId) : ''}
              onValueChange={(v) => setSelectedImageId(Number(v))}
            >
              <SelectTrigger className="w-72">
                <SelectValue placeholder="처방 이미지 선택" />
              </SelectTrigger>
              <SelectContent>
                {images.map((img) => (
                  <SelectItem key={img.imageId} value={String(img.imageId)}>
                    {img.originalFilename || img.imageCode || `이미지 ${img.imageId}`}{' '}
                    ({img.isConfirmed ? '확정' : '미확정'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedImage?.imageUrl && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openFilePreview(selectedImage.imageUrl!)}
                >
                  <ExternalLink className="mr-1 h-3 w-3" />
                  보기
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    void downloadUriFile(
                      selectedImage.imageUrl!,
                      selectedImage.originalFilename ||
                        selectedImage.imageCode ||
                        String(selectedImage.imageId),
                    ).catch((e) => handleApiError(e, toast));
                  }}
                >
                  <Download className="mr-1 h-3 w-3" />
                  다운로드
                </Button>
                <MenuWriteGate>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={actionDisabled}
                    onClick={() => setConfirmKind('deleteImage')}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    이미지 삭제
                  </Button>
                </MenuWriteGate>
              </>
            )}
          </div>
          <div className="text-sm text-muted-foreground">
            확정/등록 이미지 수:{' '}
            <span className="font-medium text-foreground">
              {confirmedCount} / {images.length}
            </span>
          </div>
        </ToolbarRow>
      </FilterCard>

      {images.length === 0 ? (
        <EmptyState description="등록된 처방 이미지가 없습니다." />
      ) : (
        <>
          <DataCard>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>의약품 코드</TableHead>
                  <TableHead>의약품 정보</TableHead>
                  <TableHead>제약사</TableHead>
                  <TableHead>의약품 명</TableHead>
                  <TableHead>약가</TableHead>
                  <TableHead>원외</TableHead>
                  <TableHead>원내</TableHead>
                  <TableHead>총 금액</TableHead>
                  <TableHead>작업</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drugLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center">
                      불러오는 중...
                    </TableCell>
                  </TableRow>
                ) : drugData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground">
                      매칭 결과가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  drugData.map((record, index) => (
                    <TableRow key={getMrRowKey(record)}>
                      <TableCell>{record.drugCode}</TableCell>
                      <TableCell>
                        {record.hasDrugInfo == null
                          ? '의약품 일괄 추가 필요'
                          : record.hasDrugInfo
                            ? 'O'
                            : 'X'}
                      </TableCell>
                      <TableCell>{record.pharmaCompanyName ?? '-'}</TableCell>
                      <TableCell>{record.drugName ?? '-'}</TableCell>
                      <TableCell>{formatMiniAmount(record.price)}</TableCell>
                      <TableCell>
                        {formatMiniAmount(record.outHospitalQuantity)}
                      </TableCell>
                      <TableCell>
                        {formatMiniAmount(record.inHospitalQuantity)}
                      </TableCell>
                      <TableCell>{formatMiniAmount(calcMrTotalAmount(record))}</TableCell>
                      <TableCell>
                        <MenuWriteGate>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actionDisabled}
                              onClick={() => {
                                setEditingDrug(record);
                                setEditingOcrIndex(
                                  record.mrId == null ? index : null,
                                );
                                setDrugDialogOpen(true);
                              }}
                            >
                              수정
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={actionDisabled}
                              onClick={() => {
                                setPendingDelete({ record, index });
                                setConfirmKind('deleteDrug');
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </MenuWriteGate>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </DataCard>

          <ToolbarRow justify="between" gap={16}>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  className="ruda-button"
                  disabled={actionDisabled || isOcrBusy}
                  onClick={handleOcr}
                >
                  <ScanLine className="mr-2 h-4 w-4" />
                  OCR
                </Button>
                <Button
                  variant="outline"
                  disabled={
                    actionDisabled ||
                    !selectedImage ||
                    isPreparingOcrImageEditor
                  }
                  onClick={handleOpenOcrImageEditor}
                >
                  {isPreparingOcrImageEditor
                    ? '좌표 준비 중...'
                    : '이미지 편집 OCR'}
                </Button>
                <Button
                  className="ruda-button"
                  disabled={
                    actionDisabled || !hasUnsavedDrugs || drugMatching
                  }
                  onClick={() => void handleDrugMatching()}
                >
                  의약품 매칭
                </Button>
                <Button
                  variant="outline"
                  disabled={actionDisabled || !hasUnsavedDrugs}
                  onClick={() => void handleBatchAdd()}
                >
                  의약품 일괄 추가
                </Button>
                <Button
                  variant="outline"
                  disabled={actionDisabled}
                  onClick={() => {
                    setEditingDrug(null);
                    setEditingOcrIndex(null);
                    setDrugDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  행 추가
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
            <MenuWriteGate>
              <ToolbarRow gap={8}>
                <Button
                  className="ruda-button"
                  disabled={
                    actionDisabled ||
                    selectedImage?.isConfirmed === true ||
                    drugData.length === 0 ||
                    hasUnsavedDrugs
                  }
                  onClick={handleConfirmImage}
                >
                  <Check className="mr-2 h-4 w-4" />
                  확정
                </Button>
              </ToolbarRow>
            </MenuWriteGate>
          </ToolbarRow>
        </>
      )}

      <MaxMatchMiniDrugDialog
        open={drugDialogOpen}
        onOpenChange={setDrugDialogOpen}
        refYm={detail?.refYm ?? ''}
        editingDrug={editingDrug}
        onSubmit={handleDrugSubmit}
      />

      <Dialog
        open={ocrFileDialogOpen}
        onOpenChange={(open) => !open && closeOcrImageEditor()}
      >
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>이미지 편집 OCR</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            선택된 처방 이미지를 편집한 뒤 OCR을 요청할 수 있습니다.
          </p>
          {isPreparingOcrImageEditor ? (
            <LoadingSpinner tip="편집 영역을 준비하는 중..." />
          ) : (
            <OcrCropWarpUploader
              maxCount={1}
              // 레거시 maxMatchMiniDetail: warp-crop + 해상도 제한 없음
              preprocessMode="warp-crop"
              maxSourceEdge={Number.POSITIVE_INFINITY}
              maxWarpWidth={Number.POSITIVE_INFINITY}
              maxWarpHeight={Number.POSITIVE_INFINITY}
              sourceImageUrl={selectedImage?.imageUrl}
              sourceImageName={`${selectedImage?.imageCode || 'prescription'}.png`}
              sourceButtonText="현재 처방 이미지 편집"
              allowFileSelect={false}
              sourceInitialPoints={ocrSourceInitialPoints}
              value={editedOcrImages}
              onChange={setEditedOcrImages}
              onSelectionChange={setOcrLiveSelection}
            />
          )}
          <FooterRow>
            <Button variant="outline" onClick={closeOcrImageEditor}>
              닫기
            </Button>
            <Button
              className="ruda-button"
              disabled={
                isPreparingOcrImageEditor ||
                editedOcrComparing ||
                !(
                  (editedOcrImages[0]?.ocrMeta?.corners?.length === 4 &&
                    editedOcrImages[0]?.ocrMeta?.bbox) ||
                  (ocrLiveSelection?.corners?.length === 4 &&
                    ocrLiveSelection?.bbox)
                )
              }
              onClick={() => void runEditedOcrComparison()}
            >
              OCR 요청 실행
            </Button>
          </FooterRow>
        </DialogContent>
      </Dialog>

      <EditedOcrComparisonModal
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        defaultRows={comparisonResults.default}
        geminiRows={comparisonResults.gemini}
        onApply={(source) => void applyEditedOcrResult(source)}
        applying={deleteAllMrs.isPending}
        previewUrl={editedOcrRequestPreviewUrl}
      />

      <Dialog
        open={showDuplicateMergeModal}
        onOpenChange={setShowDuplicateMergeModal}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>동일 처방 의약품 확인</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            아래 의약품은 코드가 중복되어 있습니다. 사용량을 합산해 각 1건으로
            등록됩니다.
          </p>
          <div className="max-h-[420px] space-y-6 overflow-y-auto">
            {duplicatedDrugGroups.map((group) => (
              <div key={group.drugCode} className="space-y-2">
                <p className="font-medium">
                  {group.drugCode} · {group.drugName || '-'}
                </p>
                <p className="text-sm text-muted-foreground">
                  {group.pharmaCompanyName || '-'} · 약가{' '}
                  {(group.price || 0).toLocaleString()}원
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>순번</TableHead>
                      <TableHead className="text-right">원외 사용량</TableHead>
                      <TableHead className="text-right">원내 사용량</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.rows.map((row, index) => (
                      <TableRow key={getMrRowKey(row)}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="text-right">
                          {Number(row.outHospitalQuantity ?? 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right">
                          {Number(row.inHospitalQuantity ?? 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell className="font-semibold">합계</TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalOutHospitalQuantity.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {group.totalInHospitalQuantity.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateMergeModal(false)}
            >
              취소
            </Button>
            <Button
              className="ruda-button"
              onClick={() => void handleConfirmDuplicateMerge()}
            >
              합산하여 등록
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={confirmKind != null}
        onOpenChange={(open) => !open && setConfirmKind(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmKind === 'deleteDrug'
                ? '삭제 확인'
                : confirmKind === 'deleteImage'
                  ? '이미지 삭제'
                  : confirmKind === 'deleteSummary'
                    ? '운영 건 삭제'
                    : confirmKind === 'deleteAll' ||
                        confirmKind === 'ocrOverwrite' ||
                        confirmKind === 'editedOcrOverwrite'
                      ? '확인'
                      : '나가기'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmKind === 'deleteDrug'
                ? '정말로 삭제하시겠습니까?'
                : confirmKind === 'deleteImage'
                  ? '선택한 처방 이미지와 등록된 의약품 정보가 모두 삭제됩니다. 삭제하시겠습니까?'
                  : confirmKind === 'deleteSummary'
                    ? '운영 건과 하위 이미지·처방 내역·결과가 모두 삭제되며 복구할 수 없습니다. 삭제하시겠습니까?'
                    : confirmKind === 'ocrOverwrite' ||
                        confirmKind === 'editedOcrOverwrite'
                      ? '의약품이 이미 등록되어 있습니다. 삭제하고 다시 등록하시겠습니까?'
                      : confirmKind === 'leave'
                        ? '저장되지 않은 정보가 있습니다. 나가시면 저장되지 않습니다.'
                        : '선택한 항목을 삭제합니다.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (confirmKind === 'deleteDrug') {
                  await handleDeleteDrug();
                } else if (confirmKind === 'deleteImage') {
                  await handleDeleteImage();
                } else if (confirmKind === 'deleteSummary') {
                  await handleDeleteSummary();
                } else if (confirmKind === 'ocrOverwrite') {
                  await handleDeleteAllMrs();
                  await runOcr();
                  setConfirmKind(null);
                } else if (confirmKind === 'editedOcrOverwrite') {
                  if (!selectedImageId || !pendingEditedOcrSource) {
                    setConfirmKind(null);
                    return;
                  }
                  try {
                    await deleteAllMrs.mutateAsync(selectedImageId);
                    applyEditedOcrRows(pendingEditedOcrSource);
                  } catch (e) {
                    handleApiError(e, toast);
                  } finally {
                    setConfirmKind(null);
                  }
                } else if (confirmKind === 'leave') {
                  if (window.history.length > 1) {
                    router.history.back();
                  } else {
                    navigate({ to: ROUTE_PATHS.MAX_MATCH_OPS } as never);
                  }
                  setConfirmKind(null);
                }
              }}
            >
              {confirmKind === 'leave' ? '나가기' : '확인'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
